import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/sonner';

/**
 * AuthCallbackPage handles the Google OAuth callback.
 * It extracts session_id from URL hash, exchanges it for a token, then redirects.
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processOAuth = async () => {
      try {
        // Extract session_id from URL hash - check both window.location and history state
        const hash = window.location.hash;
        console.log('OAuth Callback - Processing hash:', hash);
        
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');
        
        console.log('OAuth Callback - Session ID:', sessionId ? 'found' : 'not found');

        if (!sessionId) {
          setError('No session ID received from authentication provider');
          toast.error('Authentication failed: No session ID');
          setTimeout(() => { window.location.href = '/login'; }, 2000);
          return;
        }

        // Exchange session_id for token
        console.log('OAuth Callback - Exchanging session_id for token...');
        await loginWithGoogle(sessionId);
        console.log('OAuth Callback - Login successful, redirecting to dashboard');
        toast.success('Welcome to Atlas!');
        
        // Clear the hash from URL and redirect using window.location for Safari compatibility
        window.location.href = '/dashboard';
      } catch (error) {
        console.error('OAuth callback error:', error);
        setError(error.response?.data?.detail || error.message || 'Authentication failed');
        toast.error(error.response?.data?.detail || 'Authentication failed');
        setTimeout(() => { window.location.href = '/login'; }, 2000);
      }
    };

    processOAuth();
  }, [loginWithGoogle, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-callback-page">
      <div className="text-center">
        {error ? (
          <>
            <div className="text-red-500 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="font-body text-red-500 mb-2">{error}</p>
            <p className="font-body text-sm text-muted-foreground">Redirecting to login...</p>
          </>
        ) : (
          <>
            <div className="spinner mx-auto mb-4" />
            <p className="font-body text-muted-foreground">Completing sign in...</p>
          </>
        )}
      </div>
    </div>
  );
}
